import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { UserCheck, CalendarCheck, Trophy } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import StandaloneSystemHeader from "../components/branding/StandaloneSystemHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import ThemeToggle from "../components/theme/ThemeToggle";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to={location.state?.from || "/dashboard"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (r.ok) {
      navigate("/dashboard");
    } else {
      setError(r.error || "Anmeldung fehlgeschlagen.");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-background text-foreground">
      {/* Ambient layers */}
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-radial-brand" aria-hidden />

      {/* Theme toggle - top right */}
      <div className="absolute top-4 right-4 z-30">
        <ThemeToggle />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-[1.05fr_0.95fr] xl:grid-cols-[1fr_640px]">
        {/* Left – brand panel */}
        <div className="hidden lg:flex flex-col justify-between p-10 xl:p-14 relative">
          <div data-testid="login-brand-desktop" className="flex items-center">
            <StandaloneSystemHeader
              className="mb-0 justify-start"
              imageClassName="block h-auto max-h-10 w-auto max-w-[220px] object-contain"
              glowClassName="brand-glow-strong"
            />
          </div>

          <div className="max-w-xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium tracking-wider uppercase"
              style={{
                background: "color-mix(in srgb, var(--brand) 14%, transparent)",
                color: "var(--brand)",
                border: "1px solid color-mix(in srgb, var(--brand) 35%, transparent)",
              }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--brand)" }}
              />
              Dein Command Center für den Vertrieb
            </div>
            <h1 className="mt-5 font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05] text-gradient-brand">
              Alle Kontakte, Aufgaben und Deals an einem Ort.
            </h1>
            <p className="mt-4 text-base text-muted-foreground max-w-lg leading-relaxed">
              Prüfe neue Leads, plane Follow-ups und behalte laufende Deals jederzeit im Blick.
            </p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FeaturePill icon={UserCheck} label="Leads qualifizieren" />
              <FeaturePill icon={CalendarCheck} label="Follow-ups planen" />
              <FeaturePill icon={Trophy} label="Abschlüsse verfolgen" />
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground">
            © {new Date().getFullYear()} OnFyre Marketing · FyreCRM
          </div>
        </div>

        {/* Right – sign-in card */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            <div data-testid="login-brand-mobile" className="lg:hidden">
              <StandaloneSystemHeader
                className="justify-start"
                imageClassName="block h-auto max-h-9 w-auto max-w-[180px] object-contain"
              />
            </div>

            <Card className="backdrop-blur-xl">
              <CardContent className="p-6 sm:p-8">
                <div>
                  <h2 className="font-display text-2xl font-semibold tracking-tight">
                    Anmeldung
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Melden Sie sich mit Ihren Zugangsdaten an.
                  </p>
                </div>

                <form className="space-y-4 mt-6" onSubmit={handleSubmit}>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@beispiel.de"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Passwort</Label>
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  {error && (
                    <div
                      data-testid="login-error"
                      className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5"
                    >
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    data-testid="login-submit-button"
                    disabled={submitting}
                  >
                    {submitting ? "Anmeldung …" : "Anmelden"}
                  </Button>
                  <div className="text-center text-sm">
                    <Link to="/passwort-vergessen" className="text-primary underline-offset-4 hover:underline">
                      Passwort vergessen?
                    </Link>
                  </div>
                </form>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturePill({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card/60 backdrop-blur">
      <Icon className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} />
      <span className="text-xs">{label}</span>
    </div>
  );
}
